import { changedDecisionCount } from '@/pages/catalogImport/names';
import type { CatalogImportState } from '@/pages/catalogImport/store';

/**
 * What the owner did to the file, which a new file would lose, as counts:
 * the one definition of "work" that decides whether replacing the file or
 * changing the branch asks first, and what those dialogs and the file card
 * count.
 */
export type ImportWork = {
    /** Rows a drawer Save changed that are not excluded now. */
    editedRows: number;
    /** Rows excluded now; an exclusion undone since has left the set. */
    excludedRows: number;
    /** Names decisions, checked or not, that differ from their default. */
    changedNames: number;
};

/** What the work is read from, so a consumer can memoize on these six. */
export type ImportWorkSource = Pick<
    CatalogImportState,
    | 'editedRows'
    | 'exclusions'
    | 'preview'
    | 'lastNames'
    | 'decisions'
    | 'suggestions'
>;

/**
 * The work on the file in the store. An edit on a row excluded since counts
 * as an excluded row only, and a name choice needs the names that list it
 * to tell it from what the step proposed (`changedDecisionCount`, with the
 * suggestions the store remembers): the preview's, or, while a new branch
 * has dropped the preview, the latest answer's (`lastNames`), so changing
 * the branch never hides the name choices from Cancel or a new file. None
 * before any answer.
 */
export const importWorkOf = (state: ImportWorkSource): ImportWork => ({
    editedRows: [...state.editedRows].filter(
        (row) => !state.exclusions.has(row),
    ).length,
    excludedRows: state.exclusions.size,
    changedNames: changedDecisionCount(
        state.preview?.names ?? state.lastNames ?? undefined,
        state.decisions,
        state.suggestions,
    ),
});

/** Whether there is anything to lose; no work, no dialog. */
export const hasWork = (work: ImportWork): boolean =>
    work.editedRows > 0 || work.excludedRows > 0 || work.changedNames > 0;
